<script lang="ts" setup>
import ImgComponent from '@/components/media/ImgComponent.vue'
import { ApplicationMode } from '@/enums/ApplicationMode'
import { useCommonElementsStore } from '@/store/commonElements.store'
import RecomendationCard from '@/components/portfolio/RecomendationCard.vue'
import { Routes, RoutesName } from '@/enums/Routes'
import PaginationComponent from '@/components/PaginationComponent.vue'
import { IStarRatingCard } from '@/interface/portfolio/IPortfolioRecomendation'
import BasicButton from '@/components/buttons/BasicButton.vue'
import HtmlFormatter from '@/components/HtmlFormatter.vue'
import PageSection from '@/components/PageSection.vue'

const store = useCommonElementsStore()
const router = useRouter()
const config = useRuntimeConfig()
const videoAutoplay = ref<HTMLVideoElement | null>(null)

const caseStudies = store.getCaseStudies

const { params } = useRoute()
const id = ref(params.id)
const currentCaseStudy = computed(() => {
  if (!caseStudies || caseStudies.length === 0) {
    router.push(Routes.PORTFOLIO)
    return
  }
  return caseStudies.find(el => el.urlText === id.value) ?? caseStudies[0]
})
const recomendationPerPage = Math.min(
  currentCaseStudy.value?.thirdSection?.collaboration?.recomendations.length ??
  2,
  2
)

useHeadSafe({
  title: currentCaseStudy.value?.headerSection?.title ?? RoutesName.PORTFOLIO,
})

onMounted(() => {
  if (videoAutoplay.value && currentCaseStudy.value?.videoSection?.autoplay) {
    videoAutoplay.value.autoplay = true
    videoAutoplay.value.play()
  }
})

onBeforeRouteUpdate(async route => {
  id.value = route.params.id
})
console.log(currentCaseStudy.value?.videoSection?.video)
</script>

<template>
  <div>
    <HeaderComponent v-if="currentCaseStudy && currentCaseStudy.headerSection"
      :title="currentCaseStudy.headerSection.title" :describe="currentCaseStudy.headerSection.copy"
      :background="currentCaseStudy.headerSection.background" :mode="ApplicationMode.Header">
      <div class="rounded-[8px] lg:rounded-[16px] drop-shadow-lg overflow-hidden">
        <ElCard class="p-4 lg:p-14" body-style="padding: 0;">
          <section class="flex flex-col" v-if="currentCaseStudy.firstSection">
            <div class="flex flex-col lg:grid grid-cols-2 lg:gap-6 p-4 lg:p-8 relative card card--blue rounded-[8px]">
              <div>
                <h2 v-if="currentCaseStudy?.firstSection?.title"
                  class="font-bold text-[22px] lg:text-[36px] leading-[30px] lg:leading-[50px] text-[#073246]">
                  {{ currentCaseStudy.firstSection.title }}
                </h2>
                <p v-if="currentCaseStudy?.firstSection?.copy" class="font-medium text-[16px] mt-4">
                  {{ currentCaseStudy?.firstSection?.copy }}
                </p>
                <NuxtLink v-if="currentCaseStudy?.firstSection?.buttonUrl"
                  :to="currentCaseStudy?.firstSection?.buttonUrl" class="md:col-start-2">
                  <BasicButton btn-class="w-full font-semibold md:w-fit h-fit md:col-start-1 md:row-start-3 mt-5"
                    class="basic-button-position">
                    {{
                      currentCaseStudy?.firstSection?.buttonName ??
                      'See case study'
                    }}
                  </BasicButton>
                </NuxtLink>
              </div>

              <div class="flex mt-6 lg:mt-0 justify-center relative">
                <ImgComponent v-if="currentCaseStudy?.firstSection?.image" :src="currentCaseStudy?.firstSection?.image"
                  class="object-scale-down position img-width" />
              </div>
            </div>

            <ElCard class="mt-8 lg:mt-16" :body-style="{ borderRadius: '8px' }">
              <div class="flex flex-col min-[1340px]:grid grid-cols-2 lg:gap-8">
                <div class="flex flex-col">
                  <h3 class="text-[36px] font-bold text-[#073246] leading-none">
                    {{ currentCaseStudy?.firstSection?.partner?.title }}
                  </h3>
                  <div class="partner__details">
                    <HtmlFormatter :html-input="currentCaseStudy?.firstSection?.partner?.details ?? ''
                      " class="mt-4 lg:mt-[22px]" />
                  </div>
                </div>
                <HtmlFormatter :html-input="currentCaseStudy?.firstSection?.partner?.copy ?? ''
                  " class="mt-4 lg:mt-0" />
              </div>
            </ElCard>

            <PageSection v-if="currentCaseStudy?.secondSection" :title="currentCaseStudy?.secondSection?.title"
              :copy="currentCaseStudy?.secondSection?.copy" class="mt-8 lg:mt-16">
              <div class="flex flex-col lg:grid lg:grid-cols-2 lg:items-center gap-10 mt-6 lg:mt-10 w-full">
                <ImgComponent v-if="currentCaseStudy?.secondSection?.recomendation?.image"
                  :src="currentCaseStudy?.secondSection?.recomendation?.image" />
                <div class="flex flex-col w-full">
                  <div v-if="currentCaseStudy?.secondSection?.recomendation?.icons" class="flex pl-2">
                    <ImgComponent v-for="icon in currentCaseStudy?.secondSection
                      ?.recomendation?.icons" :src="icon" />
                  </div>
                  <h4 class="font-[600] text-[24px] leading-[31px] mt-4 text-left">
                    {{ currentCaseStudy?.secondSection.recomendation?.copy }}
                  </h4>
                </div>
                <div class="col-span-2 font-bold text-left">
                  <HtmlFormatter :html-input="currentCaseStudy?.secondSection?.recomendation
                      ?.additionalCopy ?? ''
                    " />
                </div>
              </div>
            </PageSection>

            <hr class="my-4 lg:my-16" v-if="currentCaseStudy?.secondSection" />

            <div class="flex flex-col lg:grid grid-cols-2 lg:items-center lg:gap-10 mt-2">
              <div v-if="currentCaseStudy?.secondSection?.propositionSection" :class="[
                'flex flex-col',
                {
                  'col-span-2':
                    !currentCaseStudy?.secondSection?.propositionSection
                      ?.image,
                },
              ]">
                <h3 class="font-bold text-[36px] leading-[50px]">
                  {{
                    currentCaseStudy?.secondSection?.propositionSection?.title
                  }}
                </h3>
                <HtmlFormatter :html-input="currentCaseStudy?.secondSection?.propositionSection?.copy
                  " class="mt-6" />
              </div>
              <ImgComponent v-if="
                currentCaseStudy?.secondSection?.propositionSection?.image
              " :src="currentCaseStudy?.secondSection?.propositionSection?.image
                  " />
            </div>

            <div v-if="currentCaseStudy?.secondSection?.techSection" class="lg:mt-16 relative">
              <ImgComponent v-if="currentCaseStudy?.secondSection?.techSection?.image"
                :src="currentCaseStudy?.secondSection?.techSection?.image"
                class="absolute w-full h-full object-cover" />
              <div class="lg:w-2/3 h-full p-4 lg:p-8 relative font-bold">
                <h3 class="text-[24px] leading-[50px] font-bold">
                  {{ currentCaseStudy?.secondSection?.techSection?.title }}
                </h3>
                <HtmlFormatter :html-input="currentCaseStudy?.secondSection?.techSection?.copy
                  " />
              </div>
            </div>

            <div v-if="currentCaseStudy?.secondSection?.techSection" class="mt-8 lg:mt-16">
              <div class="col-text">
                <HtmlFormatter :html-input="currentCaseStudy?.secondSection?.techSection?.additionalCopy
                  " />
              </div>
              <div class="flex flex-wrap w-full lg:w-[90%] mt-6 lg:mt-10 mx-auto justify-around gap-[8px]">
                <ImgComponent v-for="icon in currentCaseStudy?.secondSection?.techSection
                  ?.icons" :src="icon" class="h-[40px]" />
              </div>
            </div>
          </section>

          <hr v-if="currentCaseStudy?.secondSection?.partnerSection" class="my-8 lg:my-16" />

          <section v-if="currentCaseStudy?.secondSection?.partnerSection" class="relative">
            <ImgComponent :src="currentCaseStudy?.secondSection?.partnerSection?.image"
              class="absolute w-full h-full object-cover" />
            <div class="p-4 lg:p-6 relative">
              <h3 class="font-bold text-[30px] leading-[40px] lg:w-1/2">
                {{ currentCaseStudy?.secondSection?.partnerSection?.title }}
              </h3>
              <a :href="currentCaseStudy?.secondSection?.partnerSection?.buttonLink
                " aria-label="partner link" class="mt-4">
                {{
                  currentCaseStudy?.secondSection?.partnerSection?.buttonText
                }}
              </a>
            </div>
          </section>

          <hr v-if="currentCaseStudy?.thirdSection" class="my-8 lg:my-16" />

          <section v-if="
            currentCaseStudy?.thirdSection &&
            currentCaseStudy?.thirdSection?.expertise
          ">
            <PageSection :title="currentCaseStudy?.thirdSection?.title" :copy="currentCaseStudy?.thirdSection?.copy">
              <div class="flex flex-col lg:grid grid-cols-2 lg:items-center gap-8 mt-6 lg:mt-10">
                <ImgComponent v-if="currentCaseStudy?.thirdSection?.expertise.image"
                  :src="currentCaseStudy?.thirdSection?.expertise?.image" />
                <div :class="[
                  'flex flex-col mt-6 lg:mt-0 text-left',
                  {
                    'col-span-2':
                      !currentCaseStudy?.thirdSection?.expertise.image,
                  },
                ]">
                  <HtmlFormatter :html-input="currentCaseStudy?.thirdSection?.expertise?.copy
                    " />
                </div>
              </div>
            </PageSection>

            <hr v-if="currentCaseStudy?.thirdSection?.expertise" class="my-8 lg:my-16" />

            <div class="flex flex-col lg:grid grid-cols-2 lg:items-center gap-4 lg:gap-8">
              <div :class="[
                'flex flex-col',
                {
                  'col-span-2': !currentCaseStudy?.thirdSection?.card?.image,
                },
              ]">
                <h2 class="font-bold text-[36px] leading-[50px]">
                  {{ currentCaseStudy?.thirdSection?.card?.title }}
                </h2>
                <HtmlFormatter v-if="currentCaseStudy?.thirdSection?.card?.copy"
                  :html-input="currentCaseStudy?.thirdSection?.card?.copy" class="mt-4 lg:mt-8" />
              </div>
              <ImgComponent v-if="currentCaseStudy?.thirdSection?.card?.image"
                :src="currentCaseStudy?.thirdSection?.card?.image" />
            </div>
          </section>

          <hr v-if="currentCaseStudy?.videoSection" class="my-8 lg:my-16" />

          <section id="videoSection" v-if="currentCaseStudy?.videoSection">
            <PageSection
              :title="currentCaseStudy.videoSection.title ?? ''"
              :copy="currentCaseStudy.videoSection.copy ?? ''"
            >
              <div class="flex justify-center">
                <video
                  v-if="currentCaseStudy.videoSection.video"
                  ref="videoAutoplay"
                  :loop="currentCaseStudy.videoSection.loop"
                  :style="{ maxWidth: currentCaseStudy.videoSection.maxWidth }"
                  muted         
                  class="mt-6 lg:mt-10 rounded-[8px] w-full"
                  loading="lazy"
                >
                  <source :src="`${config.public.baseUrl}${currentCaseStudy.videoSection.video.large}`" />
                  <track kind="subtitles" />
                  <meta name="description" :content="currentCaseStudy.videoSection.copy" />
                </video>
              </div>
            </PageSection>
          </section>

          <hr v-if="currentCaseStudy?.thirdSection?.collaboration" class="my-8 lg:my-16" />

          <section id="reviews" v-if="currentCaseStudy?.thirdSection?.collaboration">
            <PageSection :title="currentCaseStudy?.thirdSection?.collaboration?.title"
              :copy="currentCaseStudy?.thirdSection?.collaboration?.copy">
              <div :class="[
                'flex flex-col lg:grid gap-4 mt-6 lg:mt-10',
                `grid-cols-${recomendationPerPage}`,
                recomendationPerPage === 1 ? 'lg:gap-0' : 'lg:gap-10',
              ]">
                <PaginationComponent v-if="
                  currentCaseStudy?.thirdSection?.collaboration
                    ?.recomendations
                " :item-list="currentCaseStudy?.thirdSection?.collaboration
                      ?.recomendations
                    " :page-size="recomendationPerPage" scroll-to="reviews" v-slot="{ items }">
                  <ElCard v-for="recommendation in (items as IStarRatingCard[])" :body-style="{ borderRadius: '8px' }">
                    <RecomendationCard :recomendation="recommendation" />
                  </ElCard>
                </PaginationComponent>
              </div>
            </PageSection>
          </section>
        </ElCard>
      </div>
    </HeaderComponent>
  </div>
</template>

<style lang="scss" scoped>
:deep(.html-formatter) {

  h4,
  p {
    font-size: 1rem;
    line-height: 22px;

    &:not(:last-of-type) {
      margin-bottom: 1rem;
    }
  }

  h3 {
    font-size: 22px;
    font-weight: bold;
    line-height: 28px;
    margin-bottom: 1rem;
  }

  h4 {
    font-weight: bold;
  }

  p {
    font-weight: medium;
  }

  ul {
    margin-left: 0.75rem;
    display: block;
  }

  &>ul {
    list-style-type: none !important;

    li {
      display: flex;
      align-items: center;
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 2rem;
    }

    li::before {
      content: '\2022';
      color: #03569a;
      font-size: 35px;
      top: 0.5rem;
      left: 0;
      position: absolute;
    }
  }

  &.font-bold {
    li {
      font-weight: bold;
      line-height: 24px;
    }
  }
}

.col-text {
  @media screen and (min-width: 1024px) {
    column-count: 2;
    column-gap: 40px;
  }
}

a,
:deep(.html-formatter a) {
  color: var(--vt-c-link);
  font-weight: bold;
  text-decoration: underline;
}

:deep(.partner__details) {
  .html-formatter p {
    color: #073246 !important;
    font-weight: 600;
  }
}

@media screen and (min-width: 1340px) {
  .position {
    top: -56px;
    height: calc(100% + 112px);
    object-fit: cover;
    position: absolute;
  }
}

@media screen and (max-width: 1023px) {
  .img-width {
    width: 100%;
  }
}
</style>
